import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Table,
  Tag,
  Input,
  InputNumber,
  Switch,
  Button,
  Alert,
  Popconfirm,
  message,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import Cookies from "js-cookie";

const API = process.env.REACT_APP_BASE_URL;

const authHeaders = () => ({
  headers: { "Content-Type": "application/json", token: Cookies.get("token") },
});

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const box = {
  border: "1px solid #eaecf0",
  borderRadius: 12,
  padding: "20px 22px",
  marginBottom: 24,
};

/**
 * PayTKS admin: manual FX rate override + webhook subscriptions.
 * PayTKS asked us to support manual rates (POST /V2/api/Rates).
 */
const PmiRates = () => {
  const { Title, Text } = Typography;

  const [health, setHealth] = useState(null);
  const [rates, setRates] = useState([]);
  const [log, setLog] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [payerCode, setPayerCode] = useState("");
  const [rateBase, setRateBase] = useState(null);
  const [commission, setCommission] = useState(0);
  const [automaticCommission, setAutomaticCommission] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, r, l, s] = await Promise.allSettled([
        axios.get(`${API}/api/pmi/health`),
        axios.get(`${API}/api/pmi/admin/rates`, authHeaders()),
        axios.get(`${API}/api/pmi/admin/rates/log`, authHeaders()),
        axios.get(`${API}/api/pmi/admin/subscriptions`, authHeaders()),
      ]);

      if (h.status === "fulfilled") setHealth(h.value.data);
      else setHealth({ ok: false, error: h.reason?.response?.data?.msg || h.reason?.message });

      if (r.status === "fulfilled") {
        setRates(r.value.data.rates || []);
        if (r.value.data.lastPayerCode) setPayerCode((p) => p || r.value.data.lastPayerCode);
      } else {
        message.error(r.reason?.response?.data?.msg || "No se pudieron cargar las tasas");
      }
      if (l.status === "fulfilled") setLog(l.value.data.log || []);
      if (s.status === "fulfilled") setSubs(s.value.data.subscriptions || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* ---------------- manual rate ---------------- */

  const submitRate = async (confirmLargeChange = false) => {
    try {
      setSaving(true);
      const { data } = await axios.post(
        `${API}/api/pmi/admin/rates`,
        {
          payerCode: payerCode.trim(),
          rateBase,
          commission,
          automaticCommission,
          currencyTo: "VES",
          confirmLargeChange,
        },
        authHeaders()
      );
      message.success(data.msg);
      setRateBase(null);
      load();
    } catch (err) {
      const d = err?.response?.data;
      if (d?.code === "LARGE_CHANGE") {
        // Almost always a typo (747.48 vs 74.748) — make the operator say so.
        if (window.confirm(`${d.msg}\n\n¿Aplicar de todas formas?`)) {
          return await submitRate(true);
        }
      } else {
        message.error(d?.msg || "No se pudo actualizar la tasa");
      }
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- webhooks ---------------- */

  const subscribe = async () => {
    try {
      setSaving(true);
      await axios.post(`${API}/api/pmi/admin/subscribe`, {}, authHeaders());
      message.success("Webhooks de órdenes y tasas suscritos");
      load();
    } catch (err) {
      message.error(err?.response?.data?.msg || "No se pudo suscribir");
    } finally {
      setSaving(false);
    }
  };

  const testSub = async (id) => {
    try {
      const { data } = await axios.post(
        `${API}/api/pmi/admin/subscriptions/${id}/test`,
        {},
        authHeaders()
      );
      message.success(data.msg, 6);
    } catch (err) {
      message.error(err?.response?.data?.msg || "La prueba falló");
    }
  };

  const removeSub = async (id) => {
    try {
      await axios.delete(`${API}/api/pmi/admin/subscriptions/${id}`, authHeaders());
      message.success("Suscripción eliminada");
      load();
    } catch (err) {
      message.error(err?.response?.data?.msg || "No se pudo eliminar");
    }
  };

  /* ---------------- tables ---------------- */

  const rateColumns = [
    { title: "ID", dataIndex: "id", width: 80 },
    { title: "Banco / ubicación", dataIndex: "location_name" },
    {
      title: "Par",
      render: (_, r) => `${r.currency_from} → ${r.currency_to}`,
      width: 120,
    },
    {
      title: "Tasa",
      dataIndex: "rate",
      width: 110,
      render: (v) => <b style={{ fontVariantNumeric: "tabular-nums" }}>{Number(v).toFixed(4)}</b>,
    },
    {
      title: "Comisión",
      width: 170,
      render: (_, r) => {
        const type = r.commission_type ?? r.commision_type;
        const amount = r.commission ?? r.commision;
        return type ? `${amount ?? 0} (${type})` : amount ?? "—";
      },
    },
  ];

  const logColumns = [
    { title: "Fecha", dataIndex: "createdAt", width: 130, render: fmtDate },
    { title: "Por", dataIndex: "by" },
    { title: "Pagador", dataIndex: "payerCode", width: 110 },
    { title: "Tasa", dataIndex: "rateBase", width: 100 },
    { title: "Comisión", dataIndex: "commission", width: 90 },
    {
      title: "Resultado",
      width: 150,
      render: (_, r) =>
        r.ok ? <Tag color="green">APLICADA</Tag> : <Tag color="volcano">ERROR</Tag>,
    },
  ];

  const subColumns = [
    { title: "ID", dataIndex: "id", width: 90 },
    {
      title: "Evento",
      dataIndex: "scope",
      render: (v) => (
        <Tag color={v === "order_changes" ? "blue" : "purple"}>
          {v === "order_changes" ? "Cambios en órdenes" : v === "rates_update" ? "Cambios de tasas" : v}
        </Tag>
      ),
    },
    {
      title: "Acciones",
      width: 220,
      render: (_, r) => (
        <>
          <Button size="small" style={{ marginRight: 8 }} onClick={() => testSub(r.id)}>
            Probar
          </Button>
          <Popconfirm
            title="¿Eliminar esta suscripción?"
            description="Dejarás de recibir avisos de PayTKS (el sondeo cada 2 min seguirá funcionando)."
            okText="Eliminar"
            cancelText="Cancelar"
            onConfirm={() => removeSub(r.id)}
          >
            <Button size="small" danger>
              Eliminar
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  const hasOrderHook = subs.some((s) => s.scope === "order_changes");
  const canSubmit = payerCode.trim() && Number(rateBase) > 0 && !saving;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Title level={window.matchMedia("(max-width: 600px)").matches ? 4 : 3}>
          PayTKS — Tasas y Webhooks
        </Title>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Actualizar
        </Button>
      </div>

      {/* ---------- health ---------- */}
      {health && !health.ok && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="No hay conexión con PayTKS"
          description={health.error || "Revisa PMI_USER / PMI_PASSWORD / PMI_BASE_URL en el servidor."}
        />
      )}
      {health?.ok && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          <Tag color="green">Conectado</Tag>
          <Tag>Crédito: {health.credit}</Tag>
          <Tag color={health.corridorReady ? "green" : "volcano"}>
            Corredor EUR→VES {health.corridorReady ? "listo" : "sin configurar"}
          </Tag>
          <Tag color={health.kycRequired ? "blue" : "orange"}>
            KYC {health.kycRequired ? "obligatorio" : "NO exigido"}
          </Tag>
          <Tag color={health.paymentMethod === "mock" ? "orange" : "green"}>
            Pasarela: {health.paymentMethod}
          </Tag>
        </div>
      )}
      {health?.paymentMethod === "mock" && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="La pasarela de pago está en modo prueba (mock)"
          description="Antes de salir a producción define PMI_PAYMENT_METHOD con el id real que aparece en /api/pmi/catalog → payments."
        />
      )}

      {/* ---------- manual rate ---------- */}
      <div style={box}>
        <Title level={5} style={{ marginTop: 0 }}>
          Tasa manual (EUR → VES)
        </Title>
        <Text type="secondary">
          Sobrescribe la tasa de PayTKS para un pagador. Si el cambio supera el 20% respecto
          a la tasa actual te pediremos confirmación.
        </Text>

        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-end", marginTop: 16 }}>
          <div>
            <div style={{ fontSize: 12, color: "#667085", marginBottom: 4 }}>Código del pagador</div>
            <Input
              style={{ width: 160 }}
              placeholder="Ej. ATEX"
              value={payerCode}
              onChange={(e) => setPayerCode(e.target.value.toUpperCase())}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: "#667085", marginBottom: 4 }}>Tasa (VES por 1 EUR)</div>
            <InputNumber
              style={{ width: 160 }}
              min={0}
              step={0.01}
              placeholder="Ej. 747.48"
              value={rateBase}
              onChange={setRateBase}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: "#667085", marginBottom: 4 }}>Comisión</div>
            <InputNumber
              style={{ width: 110 }}
              min={0}
              step={0.1}
              value={commission}
              onChange={(v) => setCommission(v ?? 0)}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: "#667085", marginBottom: 4 }}>
              Aplicar comisión contratada
            </div>
            <Switch checked={automaticCommission} onChange={setAutomaticCommission} />
          </div>
          <Button type="primary" disabled={!canSubmit} loading={saving} onClick={() => submitRate(false)}>
            Aplicar tasa
          </Button>
        </div>
      </div>

      <Title level={5}>Tasas actuales en PayTKS</Title>
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={rates}
        columns={rateColumns}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        scroll={{ x: 640 }}
        style={{ marginBottom: 28 }}
      />

      <Title level={5}>Historial de cambios manuales</Title>
      <Table
        size="small"
        rowKey="_id"
        dataSource={log}
        columns={logColumns}
        pagination={{ pageSize: 5, showSizeChanger: false }}
        scroll={{ x: 640 }}
        style={{ marginBottom: 28 }}
        locale={{ emptyText: "Todavía no hay cambios manuales" }}
      />

      {/* ---------- webhooks ---------- */}
      <div style={box}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <Title level={5} style={{ margin: 0 }}>
              Webhooks
            </Title>
            <Text type="secondary">
              PayTKS avisa aquí cuando un cliente paga o cambia el estado de una orden.
            </Text>
          </div>
          <Button type="primary" ghost loading={saving} onClick={subscribe} disabled={hasOrderHook}>
            {hasOrderHook ? "Ya suscrito" : "Suscribir órdenes y tasas"}
          </Button>
        </div>

        {!hasOrderHook && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 14 }}
            message="No hay webhook de órdenes"
            description="Sin él, los pagos se detectan por el sondeo automático (cada 2 min) en lugar de al instante."
          />
        )}

        <Table
          size="small"
          rowKey="id"
          dataSource={subs}
          columns={subColumns}
          pagination={false}
          style={{ marginTop: 14 }}
          locale={{ emptyText: "Sin suscripciones" }}
        />
      </div>
    </div>
  );
};

export default PmiRates;
